import React, { useState, useEffect } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Lock, X } from 'lucide-react'
import { useSelector } from 'react-redux'

import { UpdateUserRequestDTO } from '@/core/api/dtos/request/users/updateUser'
import { userService } from '@/core/api/services/userService'
import { useAccountPatch, useGetAccount } from '@/core/hooks/account/useAccount'
import { useAuth } from '@/core/hooks/useAuth'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { Button } from '@/shared/components/atoms/Button'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetTitle,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import { CountrySelector } from '@/shared/components/molecules/CountrySelector/CountrySelector'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { extractErrorMessage } from '@/shared/utils/helpers'

import ChangePasswordModal from './ChangePasswordModal'

interface UserProfileProps {
	onMobileEditingChange?: (isEditing: boolean) => void
	requestMobileEdit?: boolean
}

const UserProfile: React.FC<UserProfileProps> = ({
	onMobileEditingChange,
	requestMobileEdit,
}) => {
	const { t, currentLanguage } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const queryClient = useQueryClient()

	const { user } = useSelector((state: RootState) => state.auth)
	const selectedAccount = useSelector(
		(state: RootState) => state.selectedAccount.selectedAccount
	)

	const { refetchUser } = useAuth()

	// Determine which account to use: selected account or user's account
	const accountIdToUse = selectedAccount?.id || user?.account?.id
	const isAccountSelected = !!selectedAccount

	// Fetch selected account data if an account is selected
	const { data: accountData, isLoading: isLoadingAccount } = useGetAccount({
		accountId: accountIdToUse,
		options: {
			enabled: !!accountIdToUse,
		},
	})

	// Use account data from API if selected, otherwise use user's account
	const currentAccount = isAccountSelected ? accountData : user?.account

	const [formData, setFormData] = useState({
		firstname: user?.firstname || '',
		lastname: user?.lastname || '',
		phoneNumber: user?.phoneNumber || '',
		email: user?.email || '',
		accountName: currentAccount?.name || '',
		country: currentAccount?.country || '',
	})

	const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
		useState(false)
	const [isMobileEditing, setIsMobileEditing] = useState(false)

	useEffect(() => {
		onMobileEditingChange?.(isMobileEditing)
	}, [isMobileEditing, onMobileEditingChange])

	useEffect(() => {
		if (requestMobileEdit) {
			setIsMobileEditing(true)
		}
	}, [requestMobileEdit])

	// Update form data when account data changes
	useEffect(() => {
		if (currentAccount) {
			setFormData((prev) => ({
				...prev,
				accountName: currentAccount.name || '',
				country: currentAccount.country || '',
			}))
		}
	}, [currentAccount])

	// Sync user fields when user data updates
	useEffect(() => {
		setFormData((prev) => ({
			...prev,
			firstname: user?.firstname || '',
			lastname: user?.lastname || '',
			phoneNumber: user?.phoneNumber || '',
			email: user?.email || '',
		}))
	}, [user?.firstname, user?.lastname, user?.phoneNumber, user?.email])

	const updateUserMutation = useMutation({
		mutationFn: async (data: UpdateUserRequestDTO) => {
			if (!user?.id) {
				throw new Error('User ID not found')
			}
			return userService.updateUser(user.id, data)
		},
		onSuccess: () => {
			// Invalidate and refetch account data
			queryClient.invalidateQueries({ queryKey: ['account'] })
			refetchUser()
		},
		onError: (error: any) => {
			showNotification({
				type: 'error',
				message: t('account.user_profile.update_error'),
				description:
					extractErrorMessage(error) ||
					t('account.user_profile.update_error_description'),
			})
		},
	})

	const updateAccountMutation = useAccountPatch({
		accountId: accountIdToUse || 0,
		onSuccess: () => {
			// Refetch user data to update Redux state with new account info (only if not viewing selected account)
			if (!isAccountSelected) {
				refetchUser()
			}
		},
		onError: (error: any) => {
			showNotification({
				type: 'error',
				message: t('account.user_profile.update_error'),
				description:
					extractErrorMessage(error) ||
					t('account.user_profile.update_error_description'),
			})
		},
	})

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}))
	}

	const handleSave = async (): Promise<boolean> => {
		if (!accountIdToUse) {
			showNotification({
				type: 'error',
				message: t('account.user_profile.update_error'),
				description: t('account.user_profile.account_id_missing'),
			})
			return false
		}

		try {
			// Only update user fields if not viewing a selected account
			if (!isAccountSelected) {
				if (!user?.roleId) {
					showNotification({
						type: 'error',
						message: t('account.user_profile.update_error'),
						description: t('account.user_profile.role_id_missing'),
					})
					return false
				}

				const userUpdateData: UpdateUserRequestDTO = {
					account_id: user.accountId,
					firstname: formData.firstname,
					lastname: formData.lastname,
					role_id: user.roleId,
					phone_number: formData.phoneNumber || null,
				}

				await updateUserMutation.mutateAsync(userUpdateData)
			}

			// Update account fields (name and country)
			const accountUpdateData = {
				name: formData.accountName,
				country: formData.country,
			}
			await updateAccountMutation.mutateAsync(accountUpdateData)

			showNotification({
				type: 'success',
				message: t('account.user_profile.update_success'),
				description: t('account.user_profile.update_success_description'),
			})
			return true
		} catch (_error) {
			// Error handling is done in mutation onError callbacks
			return false
		}
	}

	const handleCancel = () => {
		// Reset form to original data
		setFormData({
			firstname: user?.firstname || '',
			lastname: user?.lastname || '',
			phoneNumber: user?.phoneNumber || '',
			email: user?.email || '',
			accountName: currentAccount?.name || '',
			country: currentAccount?.country || '',
		})
	}

	if (isLoadingAccount && isAccountSelected) {
		return (
			<div className="flex items-center justify-center py-12">
				<div className="text-muted-foreground">
					{t('common.loading') || 'Loading...'}
				</div>
			</div>
		)
	}

	const isSaving =
		updateUserMutation.isPending || updateAccountMutation.isPending
	const language = currentLanguage?.startsWith('es') ? 'es' : 'en'
	const countryCode = (formData.country || '').toUpperCase()
	const countryFlag =
		countryCode.length === 2
			? String.fromCodePoint(
					...countryCode.split('').map((char) => 127397 + char.charCodeAt(0))
				)
			: null
	const countryName =
		countryCode.length === 2
			? new Intl.DisplayNames([language], { type: 'region' }).of(countryCode)
			: formData.country

	const handleCloseMobileEdit = () => {
		handleCancel()
		setIsMobileEditing(false)
	}

	return (
		<div className="space-y-8">
			<div className="hidden lg:block space-y-8">
				{/* User details section - Hidden when an account is selected */}
				{!isAccountSelected && (
					<>
						<div className="space-y-6">
							<h2 className="text-lg font-semibold">
								{t('account.user_profile.user_details')}
							</h2>
							<div className="space-y-4">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="flex flex-col gap-2">
										<Label htmlFor="firstName">
											{t('account.user_profile.first_name')}
										</Label>
										<Input
											id="firstName"
											value={formData.firstname}
											onChange={(e) =>
												handleInputChange('firstname', e.target.value)
											}
											placeholder={t('account.user_profile.enter_first_name')}
										/>
									</div>
									<div className="flex flex-col gap-2">
										<Label htmlFor="lastName">
											{t('account.user_profile.last_name')}
										</Label>
										<Input
											id="lastName"
											value={formData.lastname}
											onChange={(e) =>
												handleInputChange('lastname', e.target.value)
											}
											placeholder={t('account.user_profile.enter_last_name')}
										/>
									</div>
								</div>
								<div className="flex flex-col gap-2">
									<Label htmlFor="phoneNumber">
										{t('account.user_profile.whatsapp_number')}
									</Label>
									<Input
										id="phoneNumber"
										value={formData.phoneNumber}
										onChange={(e) =>
											handleInputChange('phoneNumber', e.target.value)
										}
										placeholder={t('account.user_profile.enter_phone_number')}
									/>
								</div>
								<div className="flex flex-col gap-2">
									<Label htmlFor="email">
										{t('account.user_profile.email')}
									</Label>
									<div className="flex-col lg:flex-row flex gap-2">
										<Input
											type="email"
											id="email"
											value={formData.email}
											placeholder={t('account.user_profile.enter_email')}
											className="flex-1"
											disabled
										/>
										<Button
											variant="ghost"
											size="sm"
											onClick={() => setIsChangePasswordModalOpen(true)}
											className="rounded-full cursor-pointer border"
										>
											{t('account.user_profile.change_password')}
											<Lock className="w-4 h-4" />
										</Button>
									</div>
								</div>
							</div>
						</div>
						<Separator />
					</>
				)}

				<div className="space-y-6">
					<h2 className="text-lg font-semibold">
						{t('account.user_profile.account_details')}
					</h2>
					<div className="space-y-4">
						<div className="flex flex-col gap-2">
							<Label htmlFor="accountName">
								{t('account.user_profile.account_name')}
							</Label>
							<Input
								id="accountName"
								value={formData.accountName}
								onChange={(e) =>
									handleInputChange('accountName', e.target.value)
								}
								placeholder={t('account.user_profile.enter_account_name')}
							/>
						</div>
						<div className="flex flex-col gap-2">
							<Label htmlFor="country">
								{t('account.user_profile.country')}
							</Label>
							<CountrySelector
								value={formData.country}
								onChange={(value) => handleInputChange('country', value)}
								placeholder={t('account.user_profile.select_country')}
							/>
						</div>
					</div>
				</div>

				<div className="flex gap-3">
					<Button
						className="rounded-full cursor-pointer"
						variant="outline"
						onClick={handleCancel}
						disabled={isSaving}
					>
						{t('actions.cancel')}
					</Button>
					<Button
						className="rounded-full cursor-pointer"
						onClick={handleSave}
						disabled={isSaving}
					>
						{isSaving ? t('actions.saving') : t('actions.save')}
					</Button>
				</div>
			</div>

			<div className="lg:hidden">
				<div className="space-y-6 pb-24">
					{!isAccountSelected && (
						<>
							<div className="space-y-5">
								<h2 className="text-xl font-semibold">
									{t('account.user_profile.user_details')}
								</h2>
								<div className="space-y-4">
									<div>
										<p className="text-muted-foreground text-sm">
											{t('account.user_profile.first_name')}
										</p>
										<p className="text-lg">{formData.firstname || '-'}</p>
									</div>
									<div>
										<p className="text-muted-foreground text-sm">
											{t('account.user_profile.last_name')}
										</p>
										<p className="text-lg">{formData.lastname || '-'}</p>
									</div>
									<div>
										<p className="text-muted-foreground text-sm">
											{t('account.user_profile.whatsapp_number')}
										</p>
										<p className="text-lg">{formData.phoneNumber || '-'}</p>
									</div>
									<div>
										<p className="text-muted-foreground text-sm">
											{t('account.user_profile.email')}
										</p>
										<p className="text-lg">{formData.email || '-'}</p>
									</div>
								</div>
								<Button
									variant="outline"
									onClick={() => setIsChangePasswordModalOpen(true)}
									className="w-full rounded-full"
								>
									{t('account.user_profile.change_password')}
									<Lock className="w-4 h-4" />
								</Button>
							</div>
							<Separator />
						</>
					)}

					<div className="space-y-5">
						<h2 className="text-xl font-semibold">
							{t('account.user_profile.account_details')}
						</h2>
						<div className="space-y-4">
							<div>
								<p className="text-muted-foreground text-sm">
									{t('account.user_profile.account_name')}
								</p>
								<p className="text-lg">{formData.accountName || '-'}</p>
							</div>
							<div>
								<p className="text-muted-foreground text-sm">
									{t('account.user_profile.country')}
								</p>
								<p className="text-lg">
									{countryFlag
										? `${countryFlag} ${countryName}`
										: countryName || '-'}
								</p>
							</div>
						</div>
					</div>
				</div>

				<BottomSheet
					open={isMobileEditing}
					onOpenChange={(open) => {
						if (!open) {
							handleCloseMobileEdit()
							return
						}
						setIsMobileEditing(true)
					}}
				>
					<BottomSheetContent
						variant="card"
						className="border-border h-[90vh] px-0 pb-8"
						aria-describedby={undefined}
					>
						<div className="px-5 pt-2 space-y-8">
							<div className="flex items-center justify-between">
								<BottomSheetTitle className="text-xl md:text-2xl font-semibold">
									{t('account.user_profile.mobile.edit_profile')}
								</BottomSheetTitle>
								<Button
									variant="ghost"
									size="icon"
									onClick={handleCloseMobileEdit}
									className="h-10 w-10 rounded-full"
									aria-label={t('actions.close')}
								>
									<X className="w-5 h-5" />
								</Button>
							</div>
							<Separator />

							{!isAccountSelected && (
								<>
									<div className="space-y-5">
										<h3 className="text-xl font-semibold">
											{t('account.user_profile.user_details')}
										</h3>
										<div className="space-y-4">
											<div className="flex flex-col gap-2">
												<Label htmlFor="mobileFirstName">
													{t('account.user_profile.first_name')}
												</Label>
												<Input
													id="mobileFirstName"
													value={formData.firstname}
													onChange={(e) =>
														handleInputChange('firstname', e.target.value)
													}
													placeholder={t(
														'account.user_profile.enter_first_name'
													)}
												/>
											</div>
											<div className="flex flex-col gap-2">
												<Label htmlFor="mobileLastName">
													{t('account.user_profile.last_name')}
												</Label>
												<Input
													id="mobileLastName"
													value={formData.lastname}
													onChange={(e) =>
														handleInputChange('lastname', e.target.value)
													}
													placeholder={t(
														'account.user_profile.enter_last_name'
													)}
												/>
											</div>
											<div className="flex flex-col gap-2">
												<Label htmlFor="mobilePhoneNumber">
													{t('account.user_profile.whatsapp_number')}
												</Label>
												<Input
													id="mobilePhoneNumber"
													value={formData.phoneNumber}
													onChange={(e) =>
														handleInputChange('phoneNumber', e.target.value)
													}
													placeholder={t(
														'account.user_profile.enter_phone_number'
													)}
												/>
											</div>
										</div>
									</div>
									<Separator />
								</>
							)}

							<div className="space-y-5">
								<h3 className="text-xl font-semibold">
									{t('account.user_profile.account_details')}
								</h3>
								<div className="space-y-4">
									<div className="flex flex-col gap-2">
										<Label htmlFor="mobileAccountName">
											{t('account.user_profile.account_name')}
										</Label>
										<Input
											id="mobileAccountName"
											value={formData.accountName}
											onChange={(e) =>
												handleInputChange('accountName', e.target.value)
											}
											placeholder={t('account.user_profile.enter_account_name')}
										/>
									</div>
									<div className="flex flex-col gap-2">
										<Label htmlFor="mobileCountry">
											{t('account.user_profile.country')}
										</Label>
										<CountrySelector
											value={formData.country}
											onChange={(value) => handleInputChange('country', value)}
											placeholder={t('account.user_profile.select_country')}
										/>
									</div>
								</div>
							</div>

							<Button
								className="w-full rounded-full"
								onClick={async () => {
									const isSaved = await handleSave()
									if (isSaved) {
										setIsMobileEditing(false)
									}
								}}
								disabled={isSaving}
							>
								{isSaving ? t('actions.saving') : t('actions.save')}
							</Button>
						</div>
					</BottomSheetContent>
				</BottomSheet>
			</div>

			{/* Change Password Modal */}
			<ChangePasswordModal
				isOpen={isChangePasswordModalOpen}
				onClose={() => setIsChangePasswordModalOpen(false)}
			/>
		</div>
	)
}

export default UserProfile
